import * as angular from 'angular';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { FlamingoTypes, ReportTypes } from '@mode/shared/contract-common';
import { MixableBaseController } from '~/common/component';

import { EMap, FilterPanelActions, ReportStore } from '@mode/reports/sandbox/contract';
import { RangeValueFilterControl, LegacyReportFilterControl, ReportFilterControlRecord } from '@mode/filters/util';
import { FeatureFlagsFacade } from '@mode/shared/contract-common';
import { recordExtensionToJSObject } from '@mode/shared/util-js';
import * as LegacyEmbedDatasetSelectors from '../domain/datasets/embed-datasets.selectors';
import { EmbedReportSelectors, FlamingoSelectors } from '../domain/embed-report.selectors';

class FilterPanelContainerController extends MixableBaseController {
  static $inject = ['RxStoreService', 'FeatureFlagsFacade'];

  isEmbedViewer: boolean;

  isFilterPanelOpen$: Observable<boolean>;
  datasetsLoading$: Observable<boolean>;
  filterControls$: Observable<ReadonlyArray<ReportFilterControlRecord>>;
  orphanedFilters: Set<string>;
  filterOptions$: Observable<EMap<ReportTypes.ReportFilterOptions>>;
  filterOptionCounts$: Observable<EMap<number>>;
  filterOptionSelectedCounts$: Observable<EMap<number>>;
  model$: Observable<FlamingoTypes.ModeModel>;

  constructor(
    public rxStore: ReportStore,
    public featureFlagsFacade: FeatureFlagsFacade
  ) {
    super();
  }

  $onInit() {
    this.filterControls$ = this.rxStore.select(EmbedReportSelectors.reportFilterControls);
    this.filterOptions$ = this.rxStore.select(EmbedReportSelectors.reportFilterOptions);
    this.filterOptionCounts$ = this.rxStore.select(EmbedReportSelectors.reportFilterOptionsCount);
    this.filterOptionSelectedCounts$ = this.rxStore.select(EmbedReportSelectors.reportFilterOptionsSelectedCount);

    this.rxStore
      .select(EmbedReportSelectors.getOrphanedFilters)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((filters) => {
        this.orphanedFilters = new Set(filters.map((filter) => filter.token));
      });
    this.isFilterPanelOpen$ = this.rxStore.select(EmbedReportSelectors.isFilterPanelOpen);
    this.model$ = this.rxStore.select(FlamingoSelectors.getModel);
    this.datasetsLoading$ = this.rxStore
      .select(LegacyEmbedDatasetSelectors.datasetResultsLoadedForAllRunDatasets)
      .pipe(map((loaded) => !loaded));

    if (this.isEmbedViewer) {
      combineLatest([this.datasetsLoading$, this.filterControls$])
        .pipe(
          filter(([loading, filters]) => !loading && filters.length > 0),
          takeUntil(this.destroyed$)
        )
        .subscribe(() => {
          this.rxStore.dispatch(new FilterPanelActions.FilterPanelLoaded());
        });
    }
  }

  handleFilterPanelClose() {
    this.rxStore.dispatch(new FilterPanelActions.ToggleFilterPanel(false));
  }

  handleFiltersApply(filters: LegacyReportFilterControl[]) {
    this.rxStore.dispatch(new FilterPanelActions.ApplyFilters(filters.map((f) => recordExtensionToJSObject(f))));
  }

  handleFiltersSetDefault(filters: LegacyReportFilterControl[]) {
    this.rxStore.dispatch(new FilterPanelActions.SetAsDefault(filters.map((f) => recordExtensionToJSObject(f))));
  }

  handleFilterReset(filter: LegacyReportFilterControl) {
    this.rxStore.dispatch(new FilterPanelActions.ResetFilter(recordExtensionToJSObject(filter)));
  }

  handleFiltersReset(filters: LegacyReportFilterControl[]) {
    this.rxStore.dispatch(new FilterPanelActions.ResetFilters(filters.map((f) => recordExtensionToJSObject(f))));
  }

  handleFilterDelete(filter: LegacyReportFilterControl) {
    this.rxStore.dispatch(new FilterPanelActions.DeleteFilter(recordExtensionToJSObject(filter)));
  }

  handleFilterMoveToBottom(filter: LegacyReportFilterControl) {
    this.rxStore.dispatch(new FilterPanelActions.MoveFilterToBottom(recordExtensionToJSObject(filter)));
  }

  handleFilterMoveToPosition(filter: LegacyReportFilterControl, next: LegacyReportFilterControl) {
    this.rxStore.dispatch(new FilterPanelActions.MoveFilter(recordExtensionToJSObject(filter), next.token));
  }

  handleFilterSetType(
    filter: LegacyReportFilterControl,
    filterType: ReportTypes.ReportFilterType,
    applyFilters: boolean,
    defaultSelection: any
  ) {
    this.rxStore.dispatch(
      new FilterPanelActions.SetFilterType(
        recordExtensionToJSObject(filter),
        filterType,
        applyFilters,
        defaultSelection
      )
    );
  }

  handleFilterSetSelectControlType(filter: LegacyReportFilterControl, controlType: ReportTypes.SelectControlType) {
    this.rxStore.dispatch(
      new FilterPanelActions.SetSelectionControlType(recordExtensionToJSObject(filter), controlType)
    );
  }

  handleFilterSetRangeIncludeNulls(filter: RangeValueFilterControl, includeNulls: boolean) {
    this.rxStore.dispatch(new FilterPanelActions.SetRangeIncludeNulls(recordExtensionToJSObject(filter), includeNulls));
  }

  handleFilterHideSelectAll(filter: LegacyReportFilterControl, hideSelectAll: boolean, defaultSelection: any) {
    this.rxStore.dispatch(
      new FilterPanelActions.SetHideSelectAll(recordExtensionToJSObject(filter), hideSelectAll, defaultSelection)
    );
  }
}

export const FilterPanelContainerComponent: angular.IComponentOptions = {
  bindings: {
    isEmbedViewer: '<',
  },
  controller: FilterPanelContainerController,
  controllerAs: 'ctrl',
  template: `
    <filter-panel
      ng-cloak
      ng-class="{'is-open': (ctrl.isFilterPanelOpen$ | async:this)}"
      loading="ctrl.datasetsLoading$ | async:this"
      filters="ctrl.filterControls$ | async:this"
      filter-opts="ctrl.filterOptions$ | async:this"
      opt-counts="ctrl.filterOptionCounts$ | async:this"
      selected-opt-counts="ctrl.filterOptionSelectedCounts$ | async:this"
      model="ctrl.model$ | async:this"
      orphaned-filters="ctrl.orphanedFilters"
      on-panel-close="ctrl.handleFilterPanelClose()"

      on-filters-apply="ctrl.handleFiltersApply($filters)"
      on-filters-set-default="ctrl.handleFiltersSetDefault($filters)"
      on-filters-reset="ctrl.handleFiltersReset($filters)"

      on-filter-reset="ctrl.handleFilterReset($filter)"
      on-filter-delete="ctrl.handleFilterDelete($filter)"
      on-filter-move-to-bottom="ctrl.handleFilterMoveToBottom($filter)"
      on-filter-move-to-position="ctrl.handleFilterMoveToPosition($filter, $nextFilter)"
      on-filter-set-type="ctrl.handleFilterSetType($filter, $filterType, $applyFilters, $defaultSelection)"
      on-filter-set-select-control-type="ctrl.handleFilterSetSelectControlType($filter, $controlType)"
      on-filter-set-range-include-nulls="ctrl.handleFilterSetRangeIncludeNulls($filter, $value)"
      on-filter-hide-select-all="ctrl.handleFilterHideSelectAll($filter, $hideSelectAll, $defaultSelection)"></filter-panel>`,
};
